// composables/useGlobalRenderTracker.js
import { 
  ref, 
  reactive,
  onBeforeUpdate, 
  onUpdated, 
  onBeforeUnmount, 
  getCurrentInstance,
  watch,
  nextTick
} from 'vue'

// Global store for all component render data
const globalRenderData = reactive({
  components: new Map(),
  startTime: Date.now()
})

// Enhanced version of useRenderTracker that reports to global store
export function useGlobalRenderTracker(componentName, options = {}) {
  log.info(5,"⚡️ useGlobalRenderTracker", componentName, options)
  if (process.env.NODE_ENV !== 'development') return { renderCount: ref(0) }
  
  const {
    trackProps = true,
    trackSlots = true,
    trackEmits = true,
    logLevel = 'all', // 'all', 'summary', 'none'
    threshold = 0 // Only log renders slower than this (ms)
  } = options
  
  const renderCount = ref(0)
  const renderTimes = ref([])
  const renderTriggers = ref([])
  
  let renderStart = 0
  let previousProps = null
  let previousSlots = null
  let previousData = null
  
  const instance = getCurrentInstance()
  const name = componentName || instance?.type.name || 'Unknown Component'
  
  // Initialize global tracking for this component
  if (!globalRenderData.components.has(name)) {
    globalRenderData.components.set(name, {
      instances: 0,
      totalRenders: 0,
      totalTime: 0,
      slowRenders: 0,
      renderTimes: [],
      triggers: {},
      firstSeen: Date.now(),
      lastRender: null,
      maxRenderTime: 0,
      minRenderTime: Infinity
    })
  }
  
  const globalStats = globalRenderData.components.get(name)
  globalStats.instances++
  
  // Deep clone helper for comparison
  const deepClone = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj
    if (obj instanceof Date) return new Date(obj)
    if (obj instanceof Array) return obj.map(item => deepClone(item))
    if (typeof obj === 'object') {
      const clonedObj = {}
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          clonedObj[key] = deepClone(obj[key])
        }
      }
      return clonedObj
    }
  }
  
  // Compare objects and return differences
  const findDifferences = (prev, current, path = '') => {
    const differences = []
    
    if (prev === current) return differences
    
    if (typeof prev !== typeof current) {
      differences.push({
        path: path || 'root',
        type: 'type_change',
        from: typeof prev,
        to: typeof current,
        fromValue: prev,
        toValue: current
      })
      return differences
    }
    
    if (typeof current === 'object' && current !== null) {
      // Handle arrays
      if (Array.isArray(current)) {
        if (!Array.isArray(prev) || prev.length !== current.length) {
          differences.push({
            path: path || 'root',
            type: 'array_length',
            from: prev?.length || 0,
            to: current.length
          })
        }
        
        current.forEach((item, index) => {
          const itemPath = path ? `${path}[${index}]` : `[${index}]`
          differences.push(...findDifferences(prev?.[index], item, itemPath))
        })
        
        return differences
      }
      
      // Handle objects
      const allKeys = new Set([
        ...Object.keys(prev || {}),
        ...Object.keys(current || {})
      ])
      
      for (const key of allKeys) {
        const keyPath = path ? `${path}.${key}` : key
        
        if (!(key in prev)) {
          differences.push({
            path: keyPath,
            type: 'added',
            toValue: current[key]
          })
        } else if (!(key in current)) {
          differences.push({
            path: keyPath,
            type: 'removed',
            fromValue: prev[key]
          })
        } else {
          differences.push(...findDifferences(prev[key], current[key], keyPath))
        }
      }
    } else if (prev !== current) {
      differences.push({
        path: path || 'root',
        type: 'value_change',
        fromValue: prev,
        toValue: current
      })
    }
    
    return differences
  }
  
  // Format differences for logging
  const formatDifferences = (differences, category) => {
    if (differences.length === 0) return null
    
    const formatted = differences.map(diff => {
      switch (diff.type) {
        case 'value_change':
          return `  ${diff.path}: ${JSON.stringify(diff.fromValue)} → ${JSON.stringify(diff.toValue)}`
        case 'added':
          return `  ${diff.path}: +${JSON.stringify(diff.toValue)}`
        case 'removed':
          return `  ${diff.path}: -${JSON.stringify(diff.fromValue)}`
        case 'array_length':
          return `  ${diff.path}: length ${diff.from} → ${diff.to}`
        case 'type_change':
          return `  ${diff.path}: type ${diff.from} → ${diff.to}`
        default:
          return `  ${diff.path}: ${diff.type}`
      }
    }).join('\n')
    
    return `${category} changes:\n${formatted}`
  }
  
  onBeforeUpdate(() => {
    renderStart = performance.now()
    
    // Capture current state for comparison
    if (trackProps && instance?.props) {
      previousProps = deepClone(instance.props)
    }
    
    if (trackSlots && instance?.slots) {
      previousSlots = deepClone(instance.slots)
    }
    
    // Capture reactive data
    if (instance?.data) {
      previousData = deepClone(instance.data)
    }
  })
  
  onUpdated(() => {
    if (renderStart) {
      const renderTime = performance.now() - renderStart
      renderCount.value++
      renderTimes.value.push(renderTime)
      
      // Update global stats
      globalStats.totalRenders++
      globalStats.totalTime += renderTime
      globalStats.renderTimes.push(renderTime)
      globalStats.lastRender = Date.now()
      globalStats.maxRenderTime = Math.max(globalStats.maxRenderTime, renderTime)
      globalStats.minRenderTime = Math.min(globalStats.minRenderTime, renderTime)
      if (renderTime > 16) globalStats.slowRenders++
      
      // Analyze what triggered this render
      const triggers = []
      const triggerDetails = []
      
      // Check props changes
      if (trackProps && instance?.props && previousProps) {
        const propDiffs = findDifferences(previousProps, instance.props)
        if (propDiffs.length > 0) {
          triggers.push('props')
          globalStats.triggers.props = (globalStats.triggers.props || 0) + 1
          const formatted = formatDifferences(propDiffs, 'Props')
          if (formatted) triggerDetails.push(formatted)
        }
      }
      
      // Check slots changes
      if (trackSlots && instance?.slots && previousSlots) {
        const slotDiffs = findDifferences(previousSlots, instance.slots)
        if (slotDiffs.length > 0) {
          triggers.push('slots')
          globalStats.triggers.slots = (globalStats.triggers.slots || 0) + 1
          const formatted = formatDifferences(slotDiffs, 'Slots')
          if (formatted) triggerDetails.push(formatted)
        }
      }
      
      // Check reactive data changes
      if (instance?.data && previousData) {
        const dataDiffs = findDifferences(previousData, instance.data)
        if (dataDiffs.length > 0) {
          triggers.push('data')
          globalStats.triggers.data = (globalStats.triggers.data || 0) + 1
          const formatted = formatDifferences(dataDiffs, 'Reactive Data')
          if (formatted) triggerDetails.push(formatted)
        }
      }
      
      // If no specific triggers found, try to determine the cause
      if (triggers.length === 0) {
        // Check for common causes
        let unknownCause = 'unknown'
        
        // Check if parent component is re-rendering
        const parent = instance?.parent
        if (parent && parent.isUpdating) {
          unknownCause = 'parent_rerender'
        }
        
        // Check for route changes
        if (instance?.appContext?.app?.config?.globalProperties?.$route) {
          unknownCause = 'possible_route_change'
        }
        
        // Check for store updates (basic detection)
        if (typeof window !== 'undefined' && (window.__VUE_DEVTOOLS_GLOBAL_HOOK__ || window.$store)) {
          unknownCause = 'possible_store_update'
        }
        
        triggers.push(unknownCause)
        globalStats.triggers[unknownCause] = (globalStats.triggers[unknownCause] || 0) + 1
        triggerDetails.push(`No direct prop, slot, or data changes detected. Possible causes:
  - Parent component re-rendered
  - Force update called
  - Global state (Vuex/Pinia) changed  
  - Route changed
  - Provide/inject value changed
  - Non-reactive property modified`)
      }
      
      const renderInfo = {
        count: renderCount.value,
        time: renderTime,
        triggers: triggers,
        details: triggerDetails,
        timestamp: Date.now()
      }
      
      renderTriggers.value.push(renderInfo)
      
      // Logging based on options
      if (logLevel !== 'none' && renderTime >= threshold) {
        const triggerText = triggers.join(', ')
        
        console.group(`🔄 ${name} render #${renderCount.value}: ${renderTime.toFixed(2)}ms`)
        log.info(5,`Triggered by: ${triggerText}`)
        
        if (logLevel === 'all' && triggerDetails.length > 0) {
          triggerDetails.forEach(detail => {
            log.info(5,detail)
          })
        }
        
        // Performance warning for slow renders
        if (renderTime > 16) {
          log.info(1,`⚠️ Slow render detected! ${renderTime.toFixed(2)}ms (target: <16ms for 60fps)`)
        }
        
        console.groupEnd()
      }
    }
  })
  
  onBeforeUnmount(() => {
    globalStats.instances--
    
    // if (renderTimes.value.length > 0) {
    //   const total = renderTimes.value.reduce((a, b) => a + b, 0)
    //   const avg = total / renderTimes.value.length
    //   const max = Math.max(...renderTimes.value)
    //   const min = Math.min(...renderTimes.value)
      
    //   // Analyze trigger patterns
    //   const triggerCounts = {}
    //   renderTriggers.value.forEach(render => {
    //     render.triggers.forEach(trigger => {
    //       triggerCounts[trigger] = (triggerCounts[trigger] || 0) + 1
    //     })
    //   })
      
    //   console.group(`📊 ${name} PERFORMANCE SUMMARY`)
    //   log.info(5,`Total renders: ${renderCount.value}`)
    //   log.info(5,`Total time: ${total.toFixed(2)}ms`)
    //   log.info(5,`Average: ${avg.toFixed(2)}ms`)
    //   log.info(5,`Min: ${min.toFixed(2)}ms`)
    //   log.info(5,`Max: ${max.toFixed(2)}ms`)
    //   log.info(5,`Renders >16ms: ${renderTimes.value.filter(t => t > 16).length}`)
      
    //   log.info(5,'\nRender triggers:')
    //   Object.entries(triggerCounts)
    //     .sort(([,a], [,b]) => b - a)
    //     .forEach(([trigger, count]) => {
    //       const percentage = ((count / renderCount.value) * 100).toFixed(1)
    //       log.info(5,`  ${trigger}: ${count} (${percentage}%)`)
    //     })
      
    //   console.groupEnd()
    // }
  })
  
  // Utility methods for manual analysis
  const getSlowRenders = (threshold = 16) => {
    return renderTriggers.value.filter(render => render.time > threshold)
  }
  
  const getRendersByTrigger = (trigger) => {
    return renderTriggers.value.filter(render => 
      render.triggers.includes(trigger)
    )
  }
  
  const getStats = () => {
    if (renderTimes.value.length === 0) return null
    
    const total = renderTimes.value.reduce((a, b) => a + b, 0)
    return {
      count: renderCount.value,
      totalTime: total,
      averageTime: total / renderTimes.value.length,
      maxTime: Math.max(...renderTimes.value),
      minTime: Math.min(...renderTimes.value),
      slowRenders: renderTimes.value.filter(t => t > 16).length
    }
  }
  
  // Manual summary method you can call anytime
  const printSummary = () => {
    if (renderTimes.value.length === 0) {
      log.info(5,`📊 ${name} - No renders recorded yet`)
      return
    }
    
    const total = renderTimes.value.reduce((a, b) => a + b, 0)
    const avg = total / renderTimes.value.length
    const max = Math.max(...renderTimes.value)
    const min = Math.min(...renderTimes.value)
    
    // Analyze trigger patterns
    const triggerCounts = {}
    renderTriggers.value.forEach(render => {
      render.triggers.forEach(trigger => {
        triggerCounts[trigger] = (triggerCounts[trigger] || 0) + 1
      })
    })
    
    console.group(`📊 ${name} PERFORMANCE SUMMARY`)
    log.info(5,`Total renders: ${renderCount.value}`)
    log.info(5,`Total time: ${total.toFixed(2)}ms`)
    log.info(5,`Average: ${avg.toFixed(2)}ms`)
    log.info(5,`Min: ${min.toFixed(2)}ms`)
    log.info(5,`Max: ${max.toFixed(2)}ms`)
    log.info(5,`Renders >16ms: ${renderTimes.value.filter(t => t > 16).length}`)
    
    log.info(5,'\nRender triggers:')
    Object.entries(triggerCounts)
      .sort(([,a], [,b]) => b - a)
      .forEach(([trigger, count]) => {
        const percentage = ((count / renderCount.value) * 100).toFixed(1)
        log.info(5,`  ${trigger}: ${count} (${percentage}%)`)
      })
    
    console.groupEnd()
  }
  
  return {
    renderCount,
    renderTimes,
    renderTriggers,
    getSlowRenders,
    getRendersByTrigger,
    getStats,
    printSummary
  }
}

// Global reporting functions
export function printGlobalRenderReport() {
  const totalTime = Date.now() - globalRenderData.startTime
  const components = Array.from(globalRenderData.components.entries())
    .map(([name, stats]) => ({
      name,
      ...stats,
      avgRenderTime: stats.totalTime / stats.totalRenders || 0,
      maxRenderTime: Math.max(...stats.renderTimes, 0),
      renderFreq: stats.totalRenders / (totalTime / 1000) // renders per second
    }))
    .sort((a, b) => b.totalTime - a.totalTime) // Sort by total time
  
  console.group('🌍 GLOBAL RENDER PERFORMANCE REPORT')
  log.info(5,`Report duration: ${(totalTime / 1000).toFixed(1)}s`)
  log.info(5,`Total components tracked: ${globalRenderData.components.size}`)
  
  const totalRenders = components.reduce((sum, comp) => sum + comp.totalRenders, 0)
  const totalRenderTime = components.reduce((sum, comp) => sum + comp.totalTime, 0)
  const totalSlowRenders = components.reduce((sum, comp) => sum + comp.slowRenders, 0)
  
  log.info(5,`Total renders: ${totalRenders}`)
  log.info(5,`Total render time: ${totalRenderTime.toFixed(2)}ms`)
  log.info(5,`Slow renders (>16ms): ${totalSlowRenders}`)
  log.info(5,`Average render time: ${(totalRenderTime / totalRenders).toFixed(2)}ms`)
  
  log.info(5,'\n📊 TOP PERFORMANCE IMPACT COMPONENTS:')
  components.slice(0, 10).forEach((comp, index) => {
    log.info(5,`${index + 1}. ${comp.name}:`)
    log.info(5,`   Renders: ${comp.totalRenders} | Total time: ${comp.totalTime.toFixed(2)}ms`)
    log.info(5,`   Avg: ${comp.avgRenderTime.toFixed(2)}ms | Max: ${comp.maxRenderTime.toFixed(2)}ms`)
    log.info(5,`   Slow renders: ${comp.slowRenders} | Frequency: ${comp.renderFreq.toFixed(2)}/s`)
    
    // Show top triggers for this component
    const triggers = Object.entries(comp.triggers)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
    if (triggers.length > 0) {
      const triggerText = triggers.map(([trigger, count]) => `${trigger}(${count})`).join(', ')
      log.info(5,`   Top triggers: ${triggerText}`)
    }
  })
  
  log.info(5,'\n🚨 MOST PROBLEMATIC COMPONENTS:')
  const problematic = components
    .filter(comp => comp.slowRenders > 0 || comp.avgRenderTime > 10)
    .slice(0, 5)
  
  if (problematic.length > 0) {
    problematic.forEach(comp => {
      log.info(5,`❌ ${comp.name}: ${comp.slowRenders} slow renders, ${comp.avgRenderTime.toFixed(2)}ms avg`)
    })
  } else {
    log.info(5,'✅ No problematic components detected!')
  }
  
  log.info(5,'\n🔥 MOST FREQUENT RENDERERS:')
  const frequent = components
    .sort((a, b) => b.renderFreq - a.renderFreq)
    .slice(0, 5)
  
  frequent.forEach(comp => {
    log.info(5,`🔄 ${comp.name}: ${comp.renderFreq.toFixed(2)} renders/sec (${comp.totalRenders} total)`)
  })
  
  console.groupEnd()
  
  return {
    summary: {
      duration: totalTime,
      totalRenders,
      totalRenderTime,
      totalSlowRenders,
      avgRenderTime: totalRenderTime / totalRenders
    },
    components
  }
}

export function getTopRenderingComponents(limit = 10) {
  return Array.from(globalRenderData.components.entries())
    .map(([name, stats]) => ({ name, ...stats }))
    .sort((a, b) => b.totalRenders - a.totalRenders)
    .slice(0, limit)
}

export function getSlowestComponents(limit = 10) {
  return Array.from(globalRenderData.components.entries())
    .map(([name, stats]) => ({
      name,
      ...stats,
      avgTime: stats.totalTime / stats.totalRenders || 0
    }))
    .sort((a, b) => b.avgTime - a.avgTime)
    .slice(0, limit)
}

export function getMostProblematicComponents() {
  return Array.from(globalRenderData.components.entries())
    .map(([name, stats]) => ({
      name,
      ...stats,
      avgTime: stats.totalTime / stats.totalRenders || 0,
      problemScore: stats.slowRenders * 2 + (stats.totalTime / stats.totalRenders || 0)
    }))
    .filter(comp => comp.slowRenders > 0 || comp.avgTime > 10)
    .sort((a, b) => b.problemScore - a.problemScore)
}

export function resetGlobalRenderData() {
  globalRenderData.components.clear()
  globalRenderData.startTime = Date.now()
  log.info(5,'🔄 Global render data reset')
}

// Make available globally for console access
if (typeof window !== 'undefined') {
  window.printGlobalRenderReport = printGlobalRenderReport
  window.getTopRenderingComponents = getTopRenderingComponents
  window.getSlowestComponents = getSlowestComponents
  window.getMostProblematicComponents = getMostProblematicComponents
  window.resetGlobalRenderData = resetGlobalRenderData
  
  // log.info(5,'🌍 Global render tracking functions available:')
  // log.info(5,'  - printGlobalRenderReport()')
  // log.info(5,'  - getTopRenderingComponents(limit)')
  // log.info(5,'  - getSlowestComponents(limit)')
  // log.info(5,'  - getMostProblematicComponents()')
  // log.info(5,'  - resetGlobalRenderData()')
}